// The web UI's only import surface for the measurement engine (`serpsize/core`, built from core/src),
// plus the few UI-side helpers built on it.
import {
  loadFonts as coreLoadFonts, detailedCsvHeader, detailedCsvRows, summaryCsvHeader, summaryCsvRow,
  jsonItems, textReportPage, textReportFooter, copyAsText as coreCopyAsText,
  type FieldResult, type FontSpec, type PageResult, type Profile, type ProfileResult, type Overall, type SerpSizeConfig,
} from '../../core/src/index';

export * from '../../core/src/index';
export { parseInput, InputError } from './input';

export type Config = SerpSizeConfig;
export type Status = Exclude<Overall, 'error'>;

export const STATUS_WORD: Record<Overall, string> = { fits: 'Fits', tight: 'Tight', cut: 'Cut', error: 'Error' };

/** Load the pinned fonts; throws when any file fails, so the UI never renders with fallback widths. */
export async function loadAllFonts(specs: FontSpec[]): Promise<void> {
  const res = await coreLoadFonts(specs);
  if (res.failed.length) throw new Error(res.failed.map(f => `${f.id}: ${f.error}`).join('; '));
}

export const isErrorField = (f: FieldResult) => f.overall === 'error' || !!f.error;

/** Group a row's FieldResults into the page shape the report builders take. */
export function toPage(fields: FieldResult[], url: string): PageResult {
  const err = fields.find(isErrorField);
  return err ? { url: err.url || url, error: err.error ?? 'error', fields: [] } : { url: fields[0]?.url ?? url, fields };
}

/** Kept end of the original after Google's step-1 cut. */
export function keptEnd(f: FieldResult, config: Config): number {
  if (f.googleCut.keptEnd !== undefined) return f.googleCut.keptEnd;
  return f.googleCut.cut ? f.googleCut.sent.length - config.ellipsis.length : f.original.length;
}

/** "Copy as text" for one device: a header line, then the §6.3 inline notation. */
export function copyAsText(f: FieldResult, r: ProfileResult, profile?: Profile): string {
  return `${f.field === 'title' ? 'Title' : 'Description'} · ${profile?.label ?? r.profile} · ${STATUS_WORD[r.status]}\n${coreCopyAsText(f, r)}`;
}

export function browserEngine(): 'chromium' | 'webkit' | 'other' {
  const ua = navigator.userAgent;
  if (/Chrome\//.test(ua)) return 'chromium';
  if (/AppleWebKit/.test(ua) && /Apple/.test(navigator.vendor)) return 'webkit';
  return 'other';
}

// ---- chunked exports (spec §6.5–§6.7) ----

export function* detailedCsv(pages: Iterable<PageResult>): Generator<string> {
  yield detailedCsvHeader();
  for (const p of pages) yield detailedCsvRows(p);
}

export function* summaryCsv(pages: Iterable<PageResult>, profileIds: string[]): Generator<string> {
  yield summaryCsvHeader(profileIds);
  for (const p of pages) yield summaryCsvRow(p, profileIds);
}

export function* jsonReport(pages: Iterable<PageResult>, run: object): Generator<string> {
  yield `{"run":${JSON.stringify(run)},"results":[`;
  let first = true;
  for (const p of pages) for (const item of jsonItems(p)) { yield (first ? '\n' : ',\n') + JSON.stringify(item); first = false; }
  yield '\n]}\n';
}

export function* textReport(pages: Iterable<PageResult>, profiles: Profile[], byId: Map<string, Profile>, config: Config): Generator<string> {
  let first = true;
  for (const p of pages) { yield (first ? '' : '\n') + textReportPage(p, { profiles: byId, ellipsis: config.ellipsis }); first = false; }
  yield textReportFooter(profiles);
}
